<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";
import importantQuestions from "~/features/computer/data/important-questions-2026.json";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";

type ChapterKey =
  | "chapter_1_intro_to_software_development"
  | "chapter_2_python_programming"
  | "chapter_3_algorithms_and_problem_solving"
  | "chapter_4_computational_structures"
  | "chapter_5_data_analytics"
  | "chapter_6_emerging_technologies"
  | "chapter_7_legal_and_ethical_aspects"
  | "chapter_8_online_research_and_digital_literacy"
  | "chapter_9_entrepreneurship_in_digital_age";

type ChapterMeta = {
  key: ChapterKey;
  chapter: number;
  title: string;
  priority: string;
  limit: number;
  tone: string;
};

const chapterMeta: ChapterMeta[] = [
  {
    key: "chapter_2_python_programming",
    chapter: 2,
    title: "Python",
    priority: "CRITICAL",
    limit: 7,
    tone: "border-amber-300 bg-gradient-to-br from-amber-100 via-orange-50 to-white dark:border-amber-800 dark:from-amber-950/40 dark:via-slate-950 dark:to-slate-900",
  },
  {
    key: "chapter_3_algorithms_and_problem_solving",
    chapter: 3,
    title: "Algorithms",
    priority: "HIGH",
    limit: 5,
    tone: "border-sky-300 bg-gradient-to-br from-sky-100 via-cyan-50 to-white dark:border-sky-800 dark:from-sky-950/40 dark:via-slate-950 dark:to-slate-900",
  },
  {
    key: "chapter_1_intro_to_software_development",
    chapter: 1,
    title: "SDLC",
    priority: "HIGH",
    limit: 5,
    tone: "border-sky-300 bg-gradient-to-br from-sky-100 via-cyan-50 to-white dark:border-sky-800 dark:from-sky-950/40 dark:via-slate-950 dark:to-slate-900",
  },
  {
    key: "chapter_4_computational_structures",
    chapter: 4,
    title: "Data Structures",
    priority: "MEDIUM",
    limit: 3,
    tone: "border-stone-300 bg-gradient-to-br from-stone-100 via-stone-50 to-white dark:border-slate-700 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900",
  },
  {
    key: "chapter_5_data_analytics",
    chapter: 5,
    title: "Data Analytics",
    priority: "LOWER",
    limit: 2,
    tone: "border-slate-300 bg-gradient-to-br from-slate-100 via-white to-white dark:border-slate-700 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900",
  },
  {
    key: "chapter_6_emerging_technologies",
    chapter: 6,
    title: "Emerging Technologies",
    priority: "LOWER",
    limit: 2,
    tone: "border-slate-300 bg-gradient-to-br from-slate-100 via-white to-white dark:border-slate-700 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900",
  },
  {
    key: "chapter_7_legal_and_ethical_aspects",
    chapter: 7,
    title: "Legal and Ethical Aspects",
    priority: "LOW",
    limit: 2,
    tone: "border-slate-300 bg-gradient-to-br from-slate-100 via-white to-white dark:border-slate-700 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900",
  },
  {
    key: "chapter_8_online_research_and_digital_literacy",
    chapter: 8,
    title: "Digital Literacy",
    priority: "LOW",
    limit: 2,
    tone: "border-slate-300 bg-gradient-to-br from-slate-100 via-white to-white dark:border-slate-700 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900",
  },
  {
    key: "chapter_9_entrepreneurship_in_digital_age",
    chapter: 9,
    title: "Entrepreneurship",
    priority: "LOW",
    limit: 2,
    tone: "border-slate-300 bg-gradient-to-br from-slate-100 via-white to-white dark:border-slate-700 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900",
  },
];

const chapterLinks: Record<number, string> = {
  1: "/11th/computer/sdlc/",
  2: "/11th/computer/python/",
  3: "/11th/computer/algorithm/",
  4: "/11th/computer/data-structures/",
  5: "/11th/computer/data-analytics/",
  6: "/11th/computer/emerging-tech/",
  7: "/11th/computer/legal-ethics/",
  8: "/11th/computer/digital-literacy/",
  9: "/11th/computer/entrepreneurship/",
};

const topChapterCards = chapterMeta.map((meta) => {
  const long = importantQuestions.long_questions[meta.key] ?? [];
  const short = importantQuestions.short_questions[meta.key] ?? [];
  return {
    ...meta,
    questions: [...long, ...short].slice(0, meta.limit),
  };
});

const importantLongQuestions = chapterMeta
  .filter((item) => item.chapter <= 5)
  .map((meta) => ({
    ...meta,
    questions: (importantQuestions.long_questions[meta.key] ?? []).slice(0, Math.min(meta.limit, 4)),
  }))
  .filter((item) => item.questions.length);

const importantShortQuestions = chapterMeta
  .filter((item) => item.chapter <= 5)
  .map((meta) => ({
    ...meta,
    questions: (importantQuestions.short_questions[meta.key] ?? []).slice(0, Math.min(meta.limit, 4)),
  }))
  .filter((item) => item.questions.length);

const practicalQuestions = importantQuestions.practical_questions.chapter_2_python ?? [];

const fullQuestionGroups = chapterMeta.map((meta) => ({
  ...meta,
  long: importantQuestions.long_questions[meta.key] ?? [],
  short: importantQuestions.short_questions[meta.key] ?? [],
}));

const relatedLinks = [
  { title: "Paper Pattern", to: "/11th/computer/paper-pattern/" },
  { title: "Model Papers", to: "/11th/computer/practice/" },
  { title: "Past Papers", to: "/11th/computer/guess-paper/" },
  { title: "Chapter Notes", to: "/11th/computer/notes/" },
];

const faqs = [
  {
    question: "11th computer important questions kya hote hain?",
    answer:
      "Important questions wo selected sawalat hote hain jo pairing scheme, paper pattern aur past paper trends ke basis par shortlist kiye jate hain.",
  },
  {
    question: "1st year computer ke sab se important chapters kaun se hain?",
    answer:
      "Chapter 2 Python, Chapter 3 Algorithms aur Chapter 1 SDLC sab se important chapters hain. In se long, short aur practical prep ka main hissa cover hota hai.",
  },
  {
    question: "11th computer long questions ke liye kya prepare karein?",
    answer:
      "Long questions ke liye Chapter 2, Chapter 3, Chapter 4 aur Chapter 5 par focus karein. Python aur Algorithms sab se strong scoring route dete hain.",
  },
  {
    question: "11th computer practical questions kis chapter se aate hain?",
    answer:
      "Practical aur program-based questions zyada tar Chapter 2 Python se aate hain, is liye loops, functions aur basic programs ko ignore nahi karna chahiye.",
  },
  {
    question: "Kya sirf important questions se paper cover ho sakta hai?",
    answer:
      "Important questions se paper ka strong portion cover ho sakta hai, lekin best result ke liye pairing scheme aur MCQs ko bhi sath dekhna chahiye.",
  },
];

function openPrintDialog() {
  if (import.meta.client) {
    window.print();
  }
}

usePageSeo({
  title: "11th Computer Important Questions 2026 | Ustaad",
  description:
    "11th computer important questions 2026 page built from prioritized chapter-wise questions: Python, Algorithms, SDLC, practical questions, exam shortcut and full expandable list.",
  keywords:
    "11th computer important questions 2026, 1st year computer important questions, class 11 computer important questions",
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Important Questions", item: "/11th/computer/important-questions/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "11th Computer Important Questions 2026",
    items: [
      { name: "Chapter 1 SDLC", path: chapterLinks[1] },
      { name: "Chapter 2 Python", path: chapterLinks[2] },
      { name: "Chapter 3 Algorithms", path: chapterLinks[3] },
      { name: "Chapter 4 Data Structures", path: chapterLinks[4] },
      { name: "Paper Pattern", path: "/11th/computer/paper-pattern/" },
      { name: "Pairing Scheme", path: "/11th/computer/pairing-scheme/" },
      { name: "Guess Paper", path: "/11th/computer/guess-paper/" },
    ],
  }),
});
</script>

<template>
  <PageContainer>
    <section class="rounded-[30px] border border-slate-200 bg-gradient-to-br from-amber-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-slate-800 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 md:px-8 md:py-8 print:hidden">
      <p class="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">Filtered from chapter-wise important questions</p>
      <h1 class="mt-3 max-w-3xl text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-5xl">11th Computer Important Questions 2026</h1>

      <div class="mt-5 rounded-[24px] border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-900/40 dark:bg-emerald-950/20">
        <p class="text-sm font-medium text-slate-700 dark:text-slate-300">Agar aap sirf yeh prepare karein:</p>
        <div class="mt-3 space-y-2 text-sm leading-7 text-slate-900 dark:text-slate-100">
          <p>- Python (Chapter 2)</p>
          <p>- Algorithms (Chapter 3)</p>
          <p>- SDLC (Chapter 1)</p>
        </div>
        <p class="mt-4 text-base font-semibold text-emerald-700">60-70% paper cover ho jata hai.</p>
        <p class="mt-2 text-sm text-slate-600 dark:text-slate-300">This uses your data, but filtered for lazy-user first reading.</p>
      </div>

      <div class="mt-6 flex flex-wrap gap-3">
        <a
          href="#must-prepare"
          class="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-sky-700"
        >
          View Important Questions
        </a>
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition hover:border-sky-300 hover:text-sky-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-sky-600 dark:hover:text-sky-300"
          @click="openPrintDialog"
        >
          Download PDF
        </button>
      </div>
    </section>

    <section id="must-prepare" class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Must Prepare Chapters</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Top Important Questions Only</h2>
      </div>

      <div class="mt-5 grid gap-4 lg:grid-cols-2">
        <article
          v-for="chapter in topChapterCards"
          :key="chapter.key"
          :class="['rounded-[24px] border p-5', chapter.tone]"
        >
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">{{ chapter.priority }}</p>
              <h3 class="mt-2 text-xl font-semibold tracking-tight text-slate-950 dark:text-white">Chapter {{ chapter.chapter }} - {{ chapter.title }}</h3>
            </div>
            <NuxtLink
              :to="chapterLinks[chapter.chapter]"
              class="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700 transition hover:border-sky-300 hover:text-sky-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
            >
              Open
            </NuxtLink>
          </div>

          <div class="mt-4 space-y-2 text-sm leading-7 text-slate-700 dark:text-slate-300">
            <p v-for="question in chapter.questions" :key="question">- {{ question }}</p>
          </div>
        </article>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Long Questions</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Important Long Questions</h2>
      </div>

      <div class="mt-5 grid gap-4 lg:grid-cols-2">
        <article
          v-for="chapter in importantLongQuestions"
          :key="`long-${chapter.key}`"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">Chapter {{ chapter.chapter }} - {{ chapter.title }}</h3>
          <div class="mt-3 space-y-2 text-sm leading-7 text-slate-700 dark:text-slate-300">
            <p v-for="question in chapter.questions" :key="question">- {{ question }}</p>
          </div>
        </article>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Short Questions</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Important Short Questions</h2>
      </div>

      <div class="mt-5 grid gap-4 lg:grid-cols-2">
        <article
          v-for="chapter in importantShortQuestions"
          :key="`short-${chapter.key}`"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">Chapter {{ chapter.chapter }} - {{ chapter.title }}</h3>
          <div class="mt-3 space-y-2 text-sm leading-7 text-slate-700 dark:text-slate-300">
            <p v-for="question in chapter.questions" :key="question">- {{ question }}</p>
          </div>
        </article>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Practical Questions</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Python Only</h2>
      </div>

      <div class="mt-5 rounded-[22px] border border-amber-200 bg-amber-50 p-5 dark:border-amber-900/40 dark:bg-amber-950/20">
        <div class="space-y-2 text-sm leading-7 text-slate-700 dark:text-slate-300">
          <p v-for="question in practicalQuestions" :key="question">- {{ question }}</p>
        </div>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Expandable Full List</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">All Chapter Questions</h2>
      </div>

      <div class="mt-5 space-y-4">
        <details
          v-for="chapter in fullQuestionGroups"
          :key="`full-${chapter.key}`"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <summary class="cursor-pointer list-none text-lg font-semibold text-slate-950 dark:text-white">
            View all Chapter {{ chapter.chapter }} questions
            <span class="ml-2 text-sm font-normal text-slate-500 dark:text-slate-400">({{ chapter.title }})</span>
          </summary>

          <div class="mt-5 grid gap-4 lg:grid-cols-2">
            <div class="rounded-[18px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
              <h3 class="text-sm font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Long Questions</h3>
              <div class="mt-3 space-y-2 text-sm leading-7 text-slate-700 dark:text-slate-300">
                <p v-for="question in chapter.long" :key="`long-${chapter.key}-${question}`">- {{ question }}</p>
              </div>
            </div>

            <div class="rounded-[18px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
              <h3 class="text-sm font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Short Questions</h3>
              <div class="mt-3 space-y-2 text-sm leading-7 text-slate-700 dark:text-slate-300">
                <p v-for="question in chapter.short" :key="`short-${chapter.key}-${question}`">- {{ question }}</p>
              </div>
            </div>
          </div>
        </details>
      </div>
    </section>

    <section class="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
      <div class="rounded-[28px] border border-slate-200 bg-slate-950 p-5 text-white md:p-6">
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-sky-300">Exam Shortcut</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight">Strategy</h2>
        <div class="mt-5 space-y-4 text-sm leading-7 text-slate-200">
          <div>
            <p class="font-semibold text-white">70% ke liye:</p>
            <p>- Ch 2, 3, 1</p>
            <p>- Ch 4 basics</p>
          </div>
          <div>
            <p class="font-semibold text-white">Skip:</p>
            <p>- Ch 7, 8, 9 (sirf MCQs)</p>
          </div>
        </div>
      </div>

      <div class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Related Links</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Next Routes</h2>
        <div class="mt-5 space-y-3">
          <NuxtLink
            v-for="item in relatedLinks"
            :key="item.to"
            :to="item.to"
            class="block rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-900 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:hover:border-sky-700 dark:hover:bg-slate-900"
          >
            {{ item.title }}
          </NuxtLink>
        </div>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6 print:hidden">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">FAQ</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">11th Computer Important Questions FAQs</h2>
      </div>
      <div class="mt-5 space-y-4">
        <article
          v-for="faq in faqs"
          :key="faq.question"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ faq.question }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ faq.answer }}</p>
        </article>
      </div>
    </section>

    <section class="rounded-[28px] border border-stone-200 bg-stone-50 px-5 py-6 dark:border-slate-800 dark:bg-slate-950 md:px-8 print:hidden">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-stone-500 dark:text-slate-400">PDF Block</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Printable layout, same filtered questions</h2>
        </div>
        <div class="flex flex-wrap gap-3">
          <a
            href="#printable-important-questions"
            class="inline-flex items-center justify-center rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-700"
          >
            View PDF
          </a>
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-full border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-slate-900 transition hover:border-sky-300 hover:text-sky-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-sky-600 dark:hover:text-sky-300"
            @click="openPrintDialog"
          >
            Download PDF
          </button>
        </div>
      </div>
    </section>

    <section class="print:pt-0">
      <div
        id="printable-important-questions"
        class="mx-auto max-w-[760px] rounded-[28px] border border-stone-200 bg-white px-6 py-8 shadow-[0_18px_60px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-900 md:px-12 md:py-12 print:max-w-none print:rounded-none print:border-0 print:px-0 print:py-0 print:shadow-none"
      >
        <div class="border-b border-dashed border-stone-300 pb-6 dark:border-slate-700">
          <p class="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500 dark:text-slate-400">Printable Important Questions</p>
          <h2 class="mt-3 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">11th Computer Important Questions 2026</h2>
          <p class="mt-3 text-base leading-8 text-slate-700 dark:text-slate-300">
            Shortcut route: Python, Algorithms, aur SDLC se exam ka main portion cover hota hai. Yeh page full list nahi, filtered question weapon hai.
          </p>
        </div>

        <div class="mt-8 grid gap-4 md:grid-cols-2">
          <section class="rounded-[22px] border border-stone-200 bg-stone-50 p-5 dark:border-slate-800 dark:bg-slate-950">
            <h3 class="text-xl font-semibold text-slate-950 dark:text-white">Top Chapters</h3>
            <div class="mt-4 space-y-2 text-sm leading-7 text-slate-700 dark:text-slate-300">
              <p>Chapter 2 - Python</p>
              <p>Chapter 3 - Algorithms</p>
              <p>Chapter 1 - SDLC</p>
              <p>Chapter 4 - Data Structures basics</p>
            </div>
          </section>

          <section class="rounded-[22px] border border-stone-200 bg-stone-50 p-5 dark:border-slate-800 dark:bg-slate-950">
            <h3 class="text-xl font-semibold text-slate-950 dark:text-white">Strategy</h3>
            <div class="mt-4 space-y-2 text-sm leading-7 text-slate-700 dark:text-slate-300">
              <p>70%: Ch 2, 3, 1 + Ch 4 basics</p>
              <p>Skip deep prep: Ch 7, 8, 9</p>
              <p>Python practicals are mandatory</p>
            </div>
          </section>
        </div>
      </div>
    </section>
  </PageContainer>
</template>
